/**
 * iter355 — Informations manquantes complétées par le collaborateur au démarrage.
 * Il n'a pas accès aux fiches véhicule/client : le serveur écrit pour lui.
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const ManquantsDialog = ({ open, onClose, orId, manquants = [], onSaved }) => {
  const [valeurs, setValeurs] = useState({});
  const [busy, setBusy] = useState(false);

  const enregistrer = async () => {
    const utiles = Object.fromEntries(Object.entries(valeurs).filter(([, v]) => String(v || "").trim()));
    if (Object.keys(utiles).length === 0) { onClose?.(); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/atelier/travail/${orId}/completer`, { valeurs: utiles });
      toast.success("Informations enregistrées");
      onSaved?.(data);
      setValeurs({});
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="manquants-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertCircle className="w-4 h-4 text-amber-600" /> Informations à compléter
          </DialogTitle>
        </DialogHeader>

        <p className="text-xs text-gray-500">
          Complète ce que tu peux — tu peux aussi laisser vide et prévenir l'administration.
        </p>

        <div className="space-y-3 max-h-[55vh] overflow-y-auto">
          {manquants.map((m) => (
            <div key={m.key}>
              <Label className="text-[11px] uppercase tracking-wider text-gray-500">{m.label}</Label>
              {m.type === "textarea" ? (
                <Textarea
                  rows={2} className="resize-none"
                  value={valeurs[m.champ] ?? ""}
                  onChange={(e) => setValeurs({ ...valeurs, [m.champ]: e.target.value })}
                  data-testid={`manquant-${m.key}`}
                />
              ) : (
                <Input
                  type={m.type === "number" ? "number" : m.type === "date" ? "date" : "text"}
                  inputMode={m.type === "number" ? "numeric" : undefined}
                  className="h-11"
                  placeholder={m.valeur_proposee || ""}
                  value={valeurs[m.champ] ?? ""}
                  onChange={(e) => setValeurs({ ...valeurs, [m.champ]: e.target.value })}
                  data-testid={`manquant-${m.key}`}
                />
              )}
              {m.aide && <div className="text-[10px] text-gray-400 mt-0.5">{m.aide}</div>}
            </div>
          ))}
          {manquants.length === 0 && (
            <div className="text-sm text-gray-400 text-center py-4">Tout est complet, rien à ajouter</div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="manquants-plus-tard">Plus tard</Button>
          <Button onClick={enregistrer} disabled={busy} className="bg-[#52a675] hover:bg-[#2d5f3f]" data-testid="manquants-enregistrer">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ManquantsDialog;
